import { AccountViewListingGroupType } from '@zg-rentals/ts-rental-manager-api-client';
import {
  formatAddress,
  getPropertyTypeLabel as getPropertyTypeLabelFromType,
} from '@zg-rentals/zillow-rental-manager/utils';

import { FLEXIBLE_PROPERTIES_VIEW_MODE } from '../constants/FlexiblePropertiesConstants';
import { getDashboardOverviewRoute, getListingRoute, getPropertiesRoute } from '../constants/urls';

import stringHelper from './stringHelper';

import type {
  AccountViewListingGroupDto,
  AccountViewUnitCountsDto,
  RentalManagerAddressDto,
} from '@zg-rentals/ts-rental-manager-api-client';

export const getPropertyTypeLabel = (listing: AccountViewListingGroupDto) =>
  getPropertyTypeLabelFromType({ type: listing.type, propertyType: listing.propertyType });

export const getAddressLines = (listing: AccountViewListingGroupDto) => {
  const { address, name } = listing;
  const streetLine = address.unit ? `${address.street} #${address.unit}` : address.street || name;
  const { formattedCityStateZip } = formatAddress(address as unknown as Record<string, string>);

  return { streetLine, cityStateZip: formattedCityStateZip };
};

export const getFormattedAddress = (listing: AccountViewListingGroupDto) => {
  const { streetLine, cityStateZip } = getAddressLines(listing);

  return [streetLine, cityStateZip].filter(Boolean).join(', ');
};

export const isMultiplexListing = (listing: AccountViewListingGroupDto) =>
  listing.type === AccountViewListingGroupType.MULTIPLEX;

export const getPropertyRoute = (listing: AccountViewListingGroupDto) => {
  // A multiplex group's listingAlias points at just one unit, so drilling in would hide the rest.
  if (isMultiplexListing(listing)) {
    return getPropertiesRoute({
      viewMode: FLEXIBLE_PROPERTIES_VIEW_MODE.BOTH,
      expandedGroup: listing.groupId,
    });
  }

  if (!listing.listingAlias) {
    return null;
  }

  return listing.isMultifamily
    ? getListingRoute({ listingAlias: listing.listingAlias })
    : getDashboardOverviewRoute({ listingAlias: listing.listingAlias });
};

export const getUnitLabel = (unitCount: AccountViewUnitCountsDto | undefined) =>
  unitCount?.total != null ? stringHelper.pluralize(unitCount.total, 'unit', 'units') : null;

export const getVacantCount = (unitCount: AccountViewUnitCountsDto | undefined) => unitCount?.vacant ?? null;

// Address lines for a single unit's address, with the unit number omitted from the street line
// (the units table shows the unit separately in its own column).
export const getUnitAddressLines = (address: RentalManagerAddressDto) => {
  const { formattedStreet, formattedCityStateZip } = formatAddress({ ...address, unit: '' });

  return { streetLine: formattedStreet, cityStateZip: formattedCityStateZip };
};

/**
 * `vacantAdjustment` covers units leased since the list was fetched. The count ships with the
 * property list, so without it the header keeps the pre-add number until the next full load.
 */
export const getUnitSummary = (
  listing: AccountViewListingGroupDto & { unitCount?: AccountViewUnitCountsDto },
  vacantAdjustment = 0,
) => {
  if (listing.isMultifamily === true) {
    return '—';
  }

  const { total, vacant } = listing.unitCount ?? {};
  const unitLabel = total == null ? '— units' : stringHelper.pluralize(total, 'unit', 'units');
  const adjustedVacant = Math.max(0, (vacant ?? 0) - vacantAdjustment);
  const vacantLabel = stringHelper.pluralize(adjustedVacant, 'vacancy', 'vacancies');

  return `${unitLabel} • ${vacantLabel}`;
};
